import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import Middleware from './middleware';
import Login from '../views/Login.vue';

import loggingPage from '@/logger/logging-page';

// Import views dynamically with Webpack magic comments for better chunk naming
const loadView = (viewPath: string) => () =>
  import(/* webpackChunkName: "[request]" */ `../views/${viewPath}.vue`);

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'Home',
    component: loadView('Home'),
    meta: {
      gated: true,
      requiresAuth: true,
    },
  },
  {
    path: '/my-account',
    name: 'My Account',
    component: loadView('MyAccount'),
    meta: {
      gated: true,
      requiresAuth: true,
    },
  },
  {
    path: '/open-account',
    name: 'Open Account',
    component: loadView('OpenAccount'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/email-logs',
    name: 'Email Logs',
    component: loadView('EmailLogs'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/utilities',
    name: 'Utilities',
    component: loadView('utils/Utilities'),
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: 'currency-converter',
        name: 'Currency Converter',
        component: loadView('utils/CurrencyConverter'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'risk-profile',
        name: 'Risk Profile',
        component: loadView('utils/RiskProfile'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'glossary',
        name: 'Glossary',
        component: loadView('utils/Glossary'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/faqs',
    name: 'FAQs',
    component: loadView('utils/FAQs/FAQs'),
    children: [
      {
        path: 'investing',
        name: 'Investing FAQ',
        component: loadView('utils/FAQs/Investing'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'alerts',
        name: 'Alerts FAQ',
        component: loadView('utils/FAQs/Alerts'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'terminology',
        name: 'Terminology FAQ',
        component: loadView('utils/FAQs/Terminology'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/how-to',
    name: 'How To',
    component: loadView('utils/how-to/HowTo'),
    children: [
      {
        path: 'buy-sell',
        name: 'How to Buy Sell',
        component: loadView('utils/how-to/BuySell'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'remit-cash',
        name: 'How to Remit Cash Out',
        component: loadView('utils/how-to/RemitCash'),
        meta: {
          requiresAuth: true,
        },
      },
      {
        path: 'send-cash',
        name: 'How to Send Cash',
        component: loadView('utils/how-to/SendCash'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '*',
    name: '404',
    component: loadView('PageNotFound'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: loadView('ResetPassword'),
    meta: {
      isGuest: true,
    },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: {
      isGuest: true,
      validateAuth: true,
    },
  },
  {
    path: '/due-diligence',
    name: 'Due Diligence',
    component: loadView('DueDiligence'),
    meta: {
      gated: true,
      requiresAuth: true,
    },
  },
];

const router = new VueRouter({
  base: process.env.BASE_URL,
  mode: 'history',
  routes,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
});

router.afterEach((to, from) => {
  loggingPage.loggingOpenPage(to, from);
});

// Extracted router guard logic into separate functions for clarity
router.beforeEach(async (to, from, next) => {
  if (
    to.name !== from.name &&
    from.name !== '' &&
    from.name !== undefined &&
    from.name !== null
  ) {
    loggingPage.loggingClosePage(to, from);
  }
  await Middleware(to, from, next, router);
});

export default router;
